import React, { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { Tab } from "@headlessui/react";
import ModalIntelliPay from "./modal-intellipay";
import TshirtAndPositionStep from "../../../components/TshirtAndPositionStep";
import { useAxiosInstance } from "../../../services/Interceptor";

import { positions, tshirtSizes } from "../../../utils/PositionStateData";
import {
  getAthleteProfile,
  updateAthleteProfile,
} from "../../../services/AthleteService";
import { toast } from "react-toastify";

export default function RegisterFlowModal({
  open,
  onClose,
  athleteId,
  athleteEmail,
  eventId,
  origin,
  currentPosition,
}) {
  const apiInstance = useAxiosInstance();

  const [activeTab, setActiveTab] = useState(0);
  const [showPayment, setShowPayment] = useState(false);

  const [tshirtSize, setTshirtSize] = useState("");
  const [position, setPosition] = useState(currentPosition || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setShowPayment(false);
      setTshirtSize("");
    }
  }, [open, currentPosition]);

  useEffect(() => {
    async function fetchData() {
      const response = await getAthleteProfile(apiInstance, athleteId);
      setPosition(response.primaryPosition);
    }
    fetchData();
  }, [apiInstance, athleteId]);

  const canContinueToPayment = !!tshirtSize && !!position;

  const saveAndContinueToPayment = async () => {
    if (!canContinueToPayment) return;

    setSaving(true);
    try {
      let positionUpdated = false;

      if (position) {
        await updateAthleteProfile(apiInstance, athleteId, {
          primaryPosition: position,
        });
        positionUpdated = true;
      }

      if (positionUpdated) {
        toast.success("Position updated successfully");
      }

      setShowPayment(true);
    } catch (err) {
      toast.error("Failed to update registration details. Please try again.");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!showPayment) onClose();
      }}
      className="relative z-[250]"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/50" />

      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        {!showPayment ? (
          <DialogPanel className="w-full max-w-3xl bg-white rounded-lg shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-gray-900">
                  Complete registration details
                </div>
                <div className="text-sm text-gray-600">
                  Step: {activeTab + 1} of 2
                </div>
              </div>

              <button
                type="button"
                className="text-gray-500 hover:bg-gray-100 rounded-full p-2"
                onClick={onClose}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                <Tab.List className="flex gap-2 border-b">
                  {["T-Shirt Size", "Position"].map((label) => (
                    <Tab
                      key={label}
                      className={({ selected }) =>
                        `px-4 py-2 text-sm font-semibold -mb-[1px] border-b-2 ${
                          selected
                            ? "border-blue-600 text-blue-700"
                            : "border-transparent text-gray-600 hover:text-gray-900"
                        }`
                      }
                    >
                      {label}
                    </Tab>
                  ))}
                </Tab.List>

                <Tab.Panels className="mt-4">
                  <Tab.Panel unmount={false}>
                    <TshirtAndPositionStep
                      mode="tshirt"
                      tshirtSize={tshirtSize}
                      setTshirtSize={setTshirtSize}
                      tshirtSizes={tshirtSizes}
                      onBack={() => setActiveTab(0)}
                      onNext={() => setActiveTab(1)}
                    />
                  </Tab.Panel>

                  <Tab.Panel unmount={false}>
                    <TshirtAndPositionStep
                      mode="position"
                      position={position}
                      setPosition={setPosition}
                      positions={positions}
                      onBack={() => setActiveTab(0)}
                      onContinue={saveAndContinueToPayment}
                      saving={saving}
                      canContinue={canContinueToPayment}
                    />
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          </DialogPanel>
        ) : (
          <DialogPanel className="w-full max-w-4xl bg-transparent shadow-none">
            <ModalIntelliPay
              athleteId={athleteId}
              athleteEmail={athleteEmail}
              eventId={eventId}
              origin={origin}
              onCloseAll={onClose}
              tShirtSize={tshirtSize}
            />
          </DialogPanel>
        )}
      </div>
    </Dialog>
  );
}
